import { endpointRequest } from '../EndpointService/EndpointFunctions';

export default function createLocationService(jokiInstance, options = {}) {
    const serviceId = options.serviceId || 'LocationService';
    const joki = jokiInstance;

    const locations = new Map();

    let crudRequestActive = 0;

    function handleJokiEvents(event) {
        if (event.to === serviceId) {
            switch (event.key) {
                case 'getState':
                    return Array.from(locations.values());
                case 'get':
                    return get(event.body); //TODO: check if error handling needed
                case 'getCrudList':
                    return get();
                case 'getLocationNames':    
                    return loactationsForOrganization(event.body)
                case 'getCrudTree':
                    return getTree();
                case 'getFullCrudItem':
                    return crudGetItem(event.body.code);
                case 'getEmptyCrudItem':
                    return {
                        code: '',
                        name: '',
                        type: '',
                        status: 'Active',
                        owners: [''],
                        parentLocationCode: '',
                    };

                case 'getAvailableParents':
                    return findAvailableParents(event.body);
                case 'crudNewItem':
                    return crudNewLocation(event.body);
                case 'crudUpdateItem':
                    return crudUpdateLocation(event.body);
                case 'crudDeleteItem':
                    return crudDeleteLocations(event.body);
                default:
                    break;
            }
        }

        if (event.from === 'JOKI' && event.key === 'initialize') {
            init(event.body);
        }
        // in case for example organization filter changed
        if (event.key === 'DataResetRequired') {
            resetData();
        }
    }

    function init(eventBody) {
        // console.log('Initialize :', serviceId, eventBody);
        // loadSimpleList();
    }

    function resetData() {
        locations.clear();
        triggerServiceUpdate();
        getTree();
    }

    function get(code) {
        if (!code) {
            loadSimpleList();
            return Array.from(locations.values());
        }

        if (locations.has(code)) {
            loadLocationDetails(code);
            return locations.get(code);
        }

        return {
            error: `Location Code ${code} is not available`,
        };
    }

    function createTree(code = null) {
        if (code !== null && !locations.has(code)) return [];

        function traverseTree(obj) {
            const children = [];
            locations.forEach(item => {
                if (item.parentLocationCode !== undefined && item.parentLocationCode === obj.code) {
                    children.push(item);
                }
            });
            if (children.length > 0) {
                obj.children = children.map(traverseTree);
            }
            return obj;
        }

        if (code === null) {
            const treeRoot = [];
            locations.forEach(item => {
                if (item.parentLocationCode === undefined || item.parentLocationCode === null || item.parentLocationCode === '') {
                    treeRoot.push(traverseTree(item));
                }
            });

            return treeRoot;
        }

        if (locations.has(code)) {
            return traverseTree(locations.get(code));
        }

        return [];
    }

    function getTree(code) {
        if (!code) {
            loadSimpleList('TREE');
            return createTree();
        }

        if (locations.has(code)) {
            loadSimpleList('TREE');
            return createTree(code);
        }

        return {
            error: `Location Code ${code} is not available`,
        };
    }

    /**
     * loads a flat list of locations from the Graphql endpoint and merge them to locations map object.
     * may or may not trigger service update based on the triggerUpdate argument
     * @param {*} triggerUpdate If not passed, does not trigger service update. If "TREE" or "LIST" passed, triggers the ServiceUpdate with the same argument
     */
    async function loadSimpleList(triggerUpdate = null) { 
        joki.trigger({
            from: serviceId,
            key: 'CrudStateUpdating',
            body: true,
        });
        try {
            const results = await endpointRequest(joki, 'locations_list', {
                filters: {
                    //statuses: '[ACTIVE,RETIRED]',
                },
            });

            if (results && results.locations) {
                mergeResults(results.locations);
                if (triggerUpdate === 'TREE') {
                    triggerServiceUpdate('TREE');
                }
                if (triggerUpdate === 'LIST') {
                    triggerServiceUpdate();
                }
            } else {
                console.warn(`${serviceId}:loadSimpleList: Results for list query are invalid.\n`, results);
            }
        } catch (err) {
            console.error(`${serviceId}:loadSimpleList:\n`, err);
        }
        joki.trigger({
            from: serviceId,
            key: 'CrudStateUpdating',
            body: false,
        });        
    }

    async function loactationsForOrganization(organizationId){
        if(organizationId){
            try {
                const results = await endpointRequest(joki, 'locations_list', {
                    filters: {                        
                        organization:`"${organizationId}"`
                    },
                });
                locations.clear()
                if(results && results.locations){    
                    mergeResults(results.locations);
                    triggerServiceUpdate();
                }else{
                    console.warn(`${serviceId}:loactationsForOrganization:locations not found for given ${organizationId}`);
                }
            } catch (error) {
                console.error(`${serviceId}:loactationsForOrganization:\n`, error)
            }
        }else{
            console.error(`${serviceId}:loactationForOrganization:organizationId is required\n`)
        }
    }

    async function loadLocationDetails(code) {
        loadSimpleList();
        if (locations.has(code)) {
            return locations.get(code);
        }
        return {};
    }

    /**
     * Do not resolve until item has been loaded from the cloud.
     * @param {*} code - Location code to be loaded and returned
     */
    async function crudGetItem(code) {
        await loadSimpleList();
        const location = locations.get(code);
        if (location) {
            return location;
        }
    }

    function findAvailableParents(ownerCodes) {
        if (locations.size === 0) {
            loadSimpleList();
        }
        const availableParents = [];
        if (!ownerCodes || !Array.isArray(ownerCodes)) {
            console.error(`findAvailableParents: The incoming ownerCodes is null, undefined or not an array: ${ownerCodes} `);
        } else if (locations.size > 0) {
            for (const [key, value] of locations) {
                const owners = value.owners;
                if (owners && Array.isArray(owners) && owners.length > 0 && ownerCodes.every(o => owners.includes(o))) {
                    availableParents.push({ label: value.name || key, value: key });
                }
            }
        }
        return availableParents;
    }

    async function crudNewLocation(itemData) {
        try {
            if (locations.has(itemData.code)) {
                return null;
            }
            const epOpts = {
                body: { ...itemData },
            };
            const res = await endpointRequest(joki, 'location_new', epOpts);

            if (res === null) {
                return null;
            }

            reloadDataUntil(itemData.code);

            return res;
        } catch (err) {
            console.error('ERR', err);
            return err;
        }
    }

    function reloadDataUntil(targetCode, testFn = null, calls = 1) {
        const loopMax = 5;
        if (!locations.has(targetCode) || (typeof testFn === 'function' && testFn(locations.get(targetCode)))) {
            crudRequestActive = crudRequestActive === 0 ? 1 : crudRequestActive;
            if (calls > loopMax) {
                crudRequestActive = 0;
                joki.trigger({
                    from: serviceId,
                    key: 'CrudStateUpdating',
                    body: false,
                });
                throw new Error(
                    `${serviceId}:reloadUntilExists: No code ${targetCode} found after ${loopMax} retries. The Location was not created or is taking way too long. Please check that connections are working and cloud services are up and running.`
                );
            }
            if (crudRequestActive === 1) {
                crudRequestActive = 2;
                joki.trigger({
                    from: serviceId,
                    key: 'CrudStateUpdating',
                    body: true,
                });
            }
            get();
            setTimeout(() => {
                reloadDataUntil(targetCode, testFn, calls + 1);
            }, 3000 * calls);
        }
        crudRequestActive = 0;

        joki.trigger({
            from: serviceId,
            key: 'CrudStateUpdating',
            body: false,
        });
    }

    async function crudUpdateLocation(itemData) {
        try {
            if (!locations.has(itemData.code)) {
                throw new Error(`Cannot update an unknown Location. Code ${itemData.code} is not unknown.`);
            }

            itemData.status = itemData.status === 'Deleted' ? 'Retired' : itemData.status;
            const epOpts = {
                body: {
                    ...itemData,
                },
                urlParse: {
                    $locationId$: encodeURI(itemData.code),
                },
            };

            const originalObject = { ...locations.get(itemData.code) };
            joki.trigger({
                from: serviceId,
                key: 'CrudStateUpdating',
                body: true,
            });
            const res = await endpointRequest(joki, 'location_update', epOpts);
            joki.trigger({
                from: serviceId,
                key: 'CrudStateUpdating',
                body: false,
            });
            if (res === null) {
                console.warn(`${serviceId}:crudUpdateLocation: Location Code ${itemData.code} could not be updated as it does not exist in the cloud.`);
                return null;
            }

            reloadDataUntil(itemData.code, currentItem => {
                const origStr = Object.values(originalObject).join('|');
                const curStr = Object.values(currentItem).join('|');
                return curStr === origStr;
            });

            return res;
        } catch (err) {
            console.error(`${serviceId}:crudUpdateLocation:ERROR`, err);
            return err;
        }
    }

    async function crudDeleteLocations(itemData) {
        try {
            if (!locations.has(itemData.code)) {
                throw new Error(`No Location with Code ${itemData.code} found, cannot retire (delete) it.`);
            }
            itemData.status = 'Retired';

            const epOpts = {
                body: {
                    ...itemData,
                },
                urlParse: {
                    $locationId$: encodeURI(itemData.code),
                },
            };

            const res = await endpointRequest(joki, 'location_update', epOpts);

            locations.delete(itemData.code);
            getTree();

            setTimeout(() => {
                getTree();
            }, 1000);

            return res;
        } catch (err) {
            console.error(`${serviceId}:crudDeleteLocation:ERROR`, err);
            return err;
        }
    }

    function mergeResults(resultArray) {
        resultArray.forEach(res => {
            const item = extractOwnerIdsAndFixStatus(res);
            const code = item.code;
            if (locations.has(code)) {
                const newData = Object.assign({}, locations.get(code), item);
                locations.set(code, newData);
            } else {
                locations.set(code, item);
            }
        });
    }

    function extractOwnerIdsAndFixStatus(item) {
        if (item && typeof item === 'object') {
            if (item.owners) {
                const ownersArray = item.owners.map(owner => {
                    if (typeof owner === 'string') return owner;
                    if (owner && owner.id) return owner.id;
                });
                item.owners = ownersArray;
            } else {
                item.owners = [''];
            }

            if (item.status) {
                item.status = item.status === 'Retired' ? 'Deleted' : item.status;
            }
            return item;
        }
    }

    function triggerServiceUpdate(listName = 'LIST') {
        const results = listName === 'TREE' ? createTree() : Array.from(locations.values());
        joki.serviceUpdate(serviceId, results, { serviceUpdate: true });
    }

    joki.addService({
        id: serviceId,
        fn: handleJokiEvents,
    });
}
